/**
 * Purpose: Centralized mutation layer for owner catalogue operations (products &
 *          services). EVERY owner mutation flows through here — never through
 *          businessService directly from a hook, and never through axios from a
 *          component. This is the single chokepoint mandated for future offline
 *          support (ADR-007, D-28): a queue / retry / sync layer wraps ONE place
 *          (`dispatch`) without any caller changing.
 *
 * Layering:
 *   component → useInventory (optimistic UI state) → inventoryService
 *     (operation + payload shaping + dispatch) → businessService (HTTP) → axios
 *
 * Queue-readiness (the whole point of the seam):
 *   Each operation builds a self-contained MUTATION DESCRIPTOR
 *     { kind, businessId, targetId, payload, execute }
 *   and passes it through the private `dispatch(descriptor)` chokepoint. Today
 *   dispatch simply runs `execute()` and returns the server result. Later,
 *   dispatch can enqueue when offline and replay on reconnect, retry with
 *   backoff, dedupe, or resolve conflicts — because the descriptor carries
 *   everything needed to re-run the mutation. Callers and UI never change.
 *
 * Optimism split (deliberate): the OPTIMISTIC update + rollback live in
 *   `useInventory` (UI state is a UI concern). This service owns the operation
 *   and returns server truth; the hook reconciles. Keeping optimism out of the
 *   service is what lets the same service back both an immediate call today and
 *   a deferred queue entry tomorrow.
 *
 * Audit alignment: `kind` values are the domain event names the audit trail will
 * record (observability.md §4) — one vocabulary for mutations and their log.
 */
import { businessService } from './businessService';

/** Stable operation identifiers — also the future audit event names. */
export const MUTATION_KINDS = {
  PRODUCT_CREATE: 'product.create',
  PRODUCT_UPDATE: 'product.update',
  PRODUCT_DELETE: 'product.delete',
  PRODUCT_SET_AVAILABILITY: 'product.set_availability',
  PRODUCT_UPDATE_INVENTORY: 'product.update_inventory',
  SERVICE_CREATE: 'service.create',
  SERVICE_UPDATE: 'service.update',
  SERVICE_DELETE: 'service.delete',
  SERVICE_SET_AVAILABILITY: 'service.set_availability',
};

/**
 * THE SEAM. Executes a mutation descriptor. Currently immediate; this is the one
 * function a future offline/queue/retry layer replaces or wraps. Do not inline
 * businessService calls at call sites — always build a descriptor and dispatch.
 */
const dispatch = async (descriptor) => {
  const { execute } = descriptor;
  if (typeof execute !== 'function') {
    throw new Error(`inventoryService: mutation "${descriptor.kind}" has no executor`);
  }
  // Future: if (offline) return queue.enqueue(descriptor);
  return execute();
};

/** Builds + dispatches a descriptor. Keeps every operation one-line and uniform. */
const mutate = (kind, businessId, targetId, payload, execute) =>
  dispatch({ kind, businessId, targetId, payload, execute });

const inventoryService = {
  // ---- Products ----------------------------------------------------------
  createProduct: (businessId, data) =>
    mutate(MUTATION_KINDS.PRODUCT_CREATE, businessId, null, data, () =>
      businessService.createProduct(businessId, data),
    ),

  updateProduct: (businessId, productId, data) =>
    mutate(MUTATION_KINDS.PRODUCT_UPDATE, businessId, productId, data, () =>
      businessService.updateProduct(businessId, productId, data),
    ),

  deleteProduct: (businessId, productId) =>
    mutate(MUTATION_KINDS.PRODUCT_DELETE, businessId, productId, null, () =>
      businessService.deleteProduct(businessId, productId),
    ),

  /** Fast availability flip (JSON PATCH — no multipart, no full form). */
  setProductAvailability: (businessId, productId, isAvailable) =>
    mutate(
      MUTATION_KINDS.PRODUCT_SET_AVAILABILITY,
      businessId,
      productId,
      { is_available: isAvailable },
      () => businessService.patchProduct(businessId, productId, { is_available: isAvailable }),
    ),

  /**
   * Fast inline price/stock edit (JSON PATCH). Only the provided fields are sent,
   * so a price-only edit never touches stock and vice versa.
   */
  updateProductInventory: (businessId, productId, { price, stock_quantity } = {}) => {
    const payload = {};
    if (price !== undefined) payload.price = price;
    if (stock_quantity !== undefined) payload.stock_quantity = stock_quantity;
    return mutate(
      MUTATION_KINDS.PRODUCT_UPDATE_INVENTORY,
      businessId,
      productId,
      payload,
      () => businessService.patchProduct(businessId, productId, payload),
    );
  },

  // ---- Services ----------------------------------------------------------
  createService: (businessId, data) =>
    mutate(MUTATION_KINDS.SERVICE_CREATE, businessId, null, data, () =>
      businessService.createService(businessId, data),
    ),

  updateService: (businessId, serviceId, data) =>
    mutate(MUTATION_KINDS.SERVICE_UPDATE, businessId, serviceId, data, () =>
      businessService.updateService(businessId, serviceId, data),
    ),

  deleteService: (businessId, serviceId) =>
    mutate(MUTATION_KINDS.SERVICE_DELETE, businessId, serviceId, null, () =>
      businessService.deleteService(businessId, serviceId),
    ),

  setServiceAvailability: (businessId, serviceId, isAvailable) =>
    mutate(
      MUTATION_KINDS.SERVICE_SET_AVAILABILITY,
      businessId,
      serviceId,
      { is_available: isAvailable },
      () => businessService.patchService(businessId, serviceId, { is_available: isAvailable }),
    ),
};

export default inventoryService;
